// ABOUTME: Pure util that converts an array of resolved columns into a CSS grid-template-columns string.
// ABOUTME: Handles fixed width (column.width), flex (column.flex → Nfr), and sensible default minimums.

const DEFAULT_MIN_WIDTH_PX = 120;
const ADD_COLUMN_TRACK_PX = 48;

/**
 * Produce the CSS `grid-template-columns` value for a row.
 *
 * Rules:
 *   - column.width (number)     → fixed `${width}px` track (does NOT flex)
 *   - column.flex  (number)     → `minmax(${minWidth}px, ${flex}fr)`
 *   - column.minWidth (number)  → overrides the default 120px minimum
 *   - neither                   → `minmax(120px, 1fr)` default
 *
 * The final "+" add-column track is optional — pass `withAddColumn: true` to append it.
 *
 * @param {Array} columns  Resolved columns (ordered, visible).
 * @param {Object} [options]
 * @param {boolean} [options.withAddColumn=false]  Append the "+" sticky-right track.
 * @param {number}  [options.addColumnWidth=48]    Width of the "+" track if appended.
 * @returns {string} CSS grid-template-columns value.
 */
const columnsToGridTemplate = (columns, options = {}) => {
    const { withAddColumn = false, addColumnWidth = ADD_COLUMN_TRACK_PX } = options;

    if (!Array.isArray(columns) || columns.length === 0) {
        return withAddColumn ? `${addColumnWidth}px` : "";
    }

    const tracks = columns.map((col) => {
        // Fixed width wins over flex, same rule MUI DataGrid uses.
        if (typeof col.width === "number" && col.width > 0) {
            return `${col.width}px`;
        }
        const minWidth = typeof col.minWidth === "number" && col.minWidth > 0
            ? col.minWidth
            : DEFAULT_MIN_WIDTH_PX;
        const flex = typeof col.flex === "number" && col.flex > 0 ? col.flex : 1;
        return `minmax(${minWidth}px, ${flex}fr)`;
    });

    if (withAddColumn) {
        tracks.push(`${addColumnWidth}px`);
    }

    return tracks.join(" ");
};

/**
 * Compute accumulated left/right offsets for sticky-pinned cells.
 * Each sticky cell needs `left: Npx` (for left-pinned) or `right: Npx` (for right-pinned)
 * equal to the sum of the widths of earlier sticky siblings on the same side.
 *
 * Input:  an ordered array of resolved columns with `.pinned` set to "left" | "right" | null
 *         and `.width` / `.flex` / `.minWidth` for sizing.
 * Output: { [field]: { offsetPx: number, side: "left" | "right", isSeam: boolean } }
 *
 * Rules:
 *   - left-pinned offsets accumulate left-to-right across the pinned-left group
 *   - right-pinned offsets accumulate right-to-left across the pinned-right group
 *   - unpinned columns are not included in the result
 *   - flex columns use their minWidth for offset calculation (worst-case)
 *   - the rightmost left-pinned column and the leftmost right-pinned column are flagged
 *     as `isSeam: true` — the rendering layer uses this to put the pinned→scrollable
 *     drop-shadow on that specific cell (same approach ShDataGrid2 uses: a box-shadow
 *     on the pinned container's outer edge that bleeds into the scrollable area).
 */
const computePinnedOffsets = (columns) => {
    const offsets = {};
    if (!Array.isArray(columns)) return offsets;

    const estimateTrackWidth = (col) => {
        if (typeof col.width === "number" && col.width > 0) return col.width;
        if (typeof col.minWidth === "number" && col.minWidth > 0) return col.minWidth;
        return DEFAULT_MIN_WIDTH_PX;
    };

    // Left-pinned: walk left-to-right, accumulating. The last field hit is the seam.
    let leftAcc = 0;
    let lastLeftField = null;
    for (const col of columns) {
        if (col.pinned === "left") {
            offsets[col.field] = { offsetPx: leftAcc, side: "left", isSeam: false };
            leftAcc += estimateTrackWidth(col);
            lastLeftField = col.field;
        }
    }
    if (lastLeftField) offsets[lastLeftField].isSeam = true;

    // Right-pinned: walk right-to-left, accumulating. The last field hit (walking from
    // the right) is the leftmost right-pinned column — that's the seam.
    let rightAcc = 0;
    let lastRightField = null;
    for (let i = columns.length - 1; i >= 0; i -= 1) {
        const col = columns[i];
        if (col.pinned === "right") {
            offsets[col.field] = { offsetPx: rightAcc, side: "right", isSeam: false };
            rightAcc += estimateTrackWidth(col);
            lastRightField = col.field;
        }
    }
    if (lastRightField) offsets[lastRightField].isSeam = true;

    return offsets;
};

export default columnsToGridTemplate;
export { DEFAULT_MIN_WIDTH_PX, ADD_COLUMN_TRACK_PX, computePinnedOffsets };
